import { yupResolver } from "@hookform/resolvers/yup";
import DeleteIcon from "@mui/icons-material/Delete";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { MdmFormProps } from "../../../Forms/MdmFormProps";
import PageHeader from "../../../Layout/Header/PageHeader";
import PageHeaderButton from "../../../Layout/Header/PageHeaderButton";
import PageHeaderIconButton from "../../../Layout/Header/PageHeaderIconButton";
import { MdmTabs } from "../../../Layout/Tabs/MdmTabs";
import { BidPack, bidPackObjectName, defaultBidPack } from "../BidPack";
import { bidPackSchema } from "./bidPackSchema";
import { BidPackDestinationForm } from "./Destination/BidPackDestinationForm";
import { BidPackInformationForm } from "./Information/BidPackInformationForm";
import { BidPackOriginRegionForm } from "./OriginRegion/BidPackOriginRegionForm";
import { confirm } from "../../../Layout/Dialog/ConfirmDialog";
import { isFormSubmitForbidden } from "../../../Forms/isFormSubmitForbidden";
import { resetFormStateAndKeepFormValues } from "../../../Forms/resetFormStateAndKeepFormValues";
import { INFORMATION } from "../../../Layout/Tabs/MdmTabPanelConstants";
import { useIsUserInRoles } from "../../../Auth/useIsUserInRoles";
import { AuthorizationPolicies } from "../../../Auth/AuthorizationPolicy";

type Props = {
  title: string;
  bidPack: BidPack | undefined;
  onSave: (bidPack: BidPack) => void;
  onDelete?: (bidPackId: number | undefined) => void;
};

export const BidPackFormTabs = ({
  title,
  bidPack,
  onSave,
  onDelete,
  disabledFields,
}: Props & MdmFormProps) => {
  const isUserInAdminNetworkRole = useIsUserInRoles(AuthorizationPolicies.RequireAdminNetworkRoles);

  const methods = useForm<BidPack>({
    defaultValues: defaultBidPack,
    resolver: yupResolver(bidPackSchema),
    mode: "all",
    disabled: !isUserInAdminNetworkRole,
  });

  useEffect(() => {
    methods.reset(bidPack);

    return () => methods.reset(defaultBidPack);
  }, [bidPack, methods, methods.reset]);

  const onDeleteWithConfirmation = async () => {
    const isConfirmed = await confirm(
      `Do you really want to delete ${bidPackObjectName} with Id ${bidPack?.Id}?`
    );
    if (!isConfirmed || !onDelete) {
      return;
    }
    await onDelete(bidPack?.Id);
  };

  return (
    <>
      <PageHeader headerText={title}>
        {onDelete !== undefined && (
          <PageHeaderIconButton
            onClick={() => onDeleteWithConfirmation()}
            color="secondary"
            disabled={!isUserInAdminNetworkRole}
          >
            <DeleteIcon />
          </PageHeaderIconButton>
        )}
        <PageHeaderButton
          onClick={methods.handleSubmit((values: BidPack) => {
            onSave(values);
            resetFormStateAndKeepFormValues(methods);
          })}
          disabled={isFormSubmitForbidden(methods.formState)}
        >
          Save
        </PageHeaderButton>
      </PageHeader>

      <FormProvider {...methods}>
        <MdmTabs
          tabs={[
            {
              index: 0,
              label: INFORMATION,
              content: <BidPackInformationForm disabledFields={disabledFields} />,
            },
            {
              index: 1,
              label: "ORIGIN REGION",
              content: <BidPackOriginRegionForm />,
              disabled: !bidPack,
            },
            {
              index: 2,
              label: "DESTINATION",
              content: <BidPackDestinationForm />,
              disabled: !bidPack,
            },
          ]}
        />
      </FormProvider>
    </>
  );
};
